import { cn } from "@newxsystems/shared";
import { RavioliCardLink } from "../../../../modules/content/components/RavioliCardLink/RavioliCardLink";
import { RavioliCommonContent } from "../../../../modules/content/components/RavioliCommonContent/RavioliCommonContent";
import { RavioliMedia } from "../../../../modules/content/components/RavioliMedia/RavioliMedia";
import { RavioliRichText } from "../../../../modules/content/components/RavioliRichText/RavioliRichText";
import { RavioliSection } from "../../../../modules/content/components/RavioliSection/RavioliSection";
import type { ITestimonialGridBlockProps } from "./TestimonialGridBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;
const spans = {
  1: "sm:col-span-1",
  2: "sm:col-span-2",
  3: "sm:col-span-2 md:col-span-3",
  4: "sm:col-span-2 md:col-span-3 lg:col-span-4",
} as const;

export const TestimonialGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITestimonialGridBlockProps) => {
  if (!items?.length) return null;
  const effectiveColumnCount = Math.min(columnCount || 3, items.length) as 1 | 2 | 3 | 4;

  return (
    <RavioliSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <RavioliCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid gap-4", columns[effectiveColumnCount])}>
          {items.map((item, index) => (
            <RavioliCardLink
              className={cn(
                "flex min-h-80 flex-col justify-between border-primary p-7 text-ink",
                index === 0 ? "bg-collection" : "bg-surface",
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <blockquote>
                <RavioliRichText
                  className="font-ravioli-display text-3xl font-semibold leading-[1.08] text-ink md:text-4xl"
                  content={item.quote}
                />
              </blockquote>
              <div className="mt-10 flex items-center gap-4">
                {item.portrait ? (
                  <RavioliMedia
                    aspectRatio="1:1"
                    className="size-16 shrink-0 border-2 border-primary"
                    media={item.portrait.image}
                    sizes="56px"
                  />
                ) : null}
                <div>
                  <p className="font-ravioli-body text-sm font-semibold text-primary">
                    {item.name}
                  </p>
                  {item.attribution ? (
                    <p className="mt-1 font-ravioli-body text-[0.8125rem] text-ink-muted">
                      {item.attribution}
                    </p>
                  ) : null}
                </div>
              </div>
            </RavioliCardLink>
          ))}
        </div>
      </div>
    </RavioliSection>
  );
};

TestimonialGridBlock.displayName = "Block::TestimonialGridBlock";
